/**
 * Los símbolos de la interfaz, dibujados.
 *
 * Sustituye a los caracteres sueltos que había repartidos por el código (⚠ aparecía 23 veces, ✓
 * nueve, y luego ✗ ✕ ⟳ ● ○ ◑ ⊘ ↑ ↓ una vez cada uno). No es una cuestión de gusto: **Space
 * Grotesk no contiene ninguno de ellos**. El navegador los resuelve por su cuenta, y en varios
 * Android caen a la fuente de emoji, que los pinta EN COLOR. Eso rompe a la vez la voz
 * tipográfica y la promesa de que nada se comunica solo por color: un ⚠ naranja de emoji en una
 * pantalla que se está mirando en escala de grises es un cuadrado.
 *
 * Además, un lector de pantalla lee el carácter en voz alta —«signo de admiración dentro de un
 * triángulo»— en mitad de la frase.
 *
 * Por eso van dibujados, siempre `aria-hidden`, y **siempre acompañados de la palabra**, que es
 * la que lleva el significado y la que está traducida.
 */

export type NombreGlifo =
  | 'aviso'
  | 'correcto'
  | 'incorrecto'
  | 'cerrar'
  | 'girando'
  | 'lleno'
  | 'vacio'
  | 'medio'
  | 'prohibido'
  | 'arriba'
  | 'abajo'
  | 'pausa'
  | 'reanudar'
  | 'idioma'
  | 'atras'
  | 'papelera'
  | 'ajustes';

/**
 * Cada trazo se dibuja dentro de una caja de 24, con el trazo a 2,5 y las puntas redondeadas.
 *
 * Todos comparten caja y grosor a propósito: mezclados en una fila —el aviso junto al aspa, la
 * marca junto a la flecha— tienen que pesar lo mismo, y eso no se consigue si cada uno viene de
 * una fuente distinta.
 */
const TRAZOS: Record<NombreGlifo, React.ReactNode> = {
  aviso: (
    <>
      <path d="M12 3.5 2.5 20.5h19L12 3.5Z" />
      <path d="M12 9.5v4.5" />
      <path d="M12 17.2v.1" />
    </>
  ),
  correcto: <path d="m4 12.5 5.5 5.5L20 6.5" />,
  incorrecto: (
    <>
      <path d="M12 2.5a9.5 9.5 0 1 0 0 19 9.5 9.5 0 0 0 0-19Z" />
      <path d="M8.5 8.5 15.5 15.5" />
      <path d="M15.5 8.5 8.5 15.5" />
    </>
  ),
  cerrar: (
    <>
      <path d="M5 5 19 19" />
      <path d="M19 5 5 19" />
    </>
  ),
  // El giro lo pone quien lo usa, con la clase `animate-spin`: aquí solo está la forma.
  girando: <path d="M12 3.5a8.5 8.5 0 1 0 8.5 8.5" />,
  lleno: <circle cx="12" cy="12" r="7" fill="currentColor" stroke="none" />,
  vacio: <circle cx="12" cy="12" r="7" />,
  medio: (
    <>
      <circle cx="12" cy="12" r="7" />
      <path d="M12 5a7 7 0 0 1 0 14Z" fill="currentColor" stroke="none" />
    </>
  ),
  prohibido: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M6 6 18 18" />
    </>
  ),
  arriba: <path d="M12 19V5m0 0-6 6m6-6 6 6" />,
  abajo: <path d="M12 5v14m0 0 6-6m-6 6-6-6" />,
  pausa: (
    <>
      <path d="M9 5v14" />
      <path d="M15 5v14" />
    </>
  ),
  reanudar: <path d="M7 4.5v15l13-7.5-13-7.5Z" />,
  /* Un globo terráqueo: meridiano, paralelo y perfil. Es lo que se entiende sin leer. */
  idioma: (
    <>
      <circle cx="12" cy="12" r="8.75" />
      <path d="M3.25 12h17.5" />
      <path d="M12 3.25c2.4 2.5 3.6 5.4 3.6 8.75S14.4 18.25 12 20.75c-2.4-2.5-3.6-5.4-3.6-8.75S9.6 5.75 12 3.25Z" />
    </>
  ),
  atras: (
    <>
      <path d="M20 12H4.5" />
      <path d="M11 5.5 4.5 12l6.5 6.5" />
    </>
  ),
  papelera: (
    <>
      <path d="M4 6.5h16" />
      <path d="M9.5 6.5V4.25h5V6.5" />
      <path d="M6.5 6.5 7.5 20h9l1-13.5" />
    </>
  ),
  ajustes: (
    <>
      <path d="M4 7.5h16" />
      <path d="M4 16.5h16" />
      <circle cx="9.5" cy="7.5" r="2.6" />
      <circle cx="15" cy="16.5" r="2.6" />
    </>
  ),
};

export interface GlifoProps {
  readonly nombre: NombreGlifo;
  /** Lado de la caja en píxeles. Por defecto acompaña a un texto de cuerpo. */
  readonly size?: number;
  readonly className?: string | undefined;
}

export function Glifo({ nombre, size = 20, className }: GlifoProps): React.JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      // Lo que significa lo dice la palabra que va al lado, que además está traducida.
      aria-hidden="true"
      focusable="false"
      className={className}
      // `flex-shrink-0` evita que se aplaste cuando la etiqueta de al lado es larga y la caja
      // está en un contenedor flexible, que es prácticamente siempre.
      style={{ flexShrink: 0 }}
    >
      {TRAZOS[nombre]}
    </svg>
  );
}
